import { styled } from "goober";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { formatTinesUnits } from "../../lib/formatTinesUnits";
import { routes } from "../../lib/urls";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { PressButton } from "../PressButton";
import { Text } from "../Text";
import { ControlButton } from "./ControlButton";
import {
  Bar,
  BarColumn,
  BarRow,
  barColumnStyles,
  ColumnMeta,
  FadeScrollColumns,
  logBarFraction,
} from "./monitoringBars";
import { spaceColorMap } from "./monitoringPanelLayout";
import {
  foldsPersonal,
  PERSONAL_BUCKET,
  PERSONAL_BUCKET_COLOR,
} from "./monitoringScope";
import { UnitsChartCard, unitsMonthLabel } from "./tinesUnitsChartLayout";
import {
  buildTinesUnitsBySpaceRows,
  UNATTRIBUTED_UNITS_BUCKET,
} from "./tinesUnitsBySpaceRows";
import { buildTinesUnitsTrendChart } from "./tinesUnitsTrendRows";
import { useMonitoringScope } from "./useMonitoringScope";
import {
  useMonitoringSpaces,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { PanelHeader, TotalRow } from "./workflowsLayout";

const Header = styled("div")`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const Column = styled(PressButton)`
  ${barColumnStyles}
`;

const Chart = styled("div")`
  display: flex;
  flex: 1;
  min-height: 0;

  & > div {
    min-height: 0;
    width: 100%;
    max-width: none;
  }

  [data-bar-column] {
    flex: 1 0 64px;
    max-width: none;
  }
`;

const SpaceBarRow = styled(BarRow)`
  min-height: 0;
`;

const Empty = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export function TinesUnitsBySpace({
  periodStart,
  onSelectMonth,
  onOpenSpace,
}: {
  periodStart: number;
  onSelectMonth: (periodStart: number) => void;
  onOpenSpace: (spaceId: string) => void;
}) {
  const { report } = useMonitoringWorkflows();
  const { spaces } = useMonitoringSpaces();
  const { scope, personalIds, matchesSpace } = useMonitoringScope(
    routes.monitoring.workflows
  );
  const displayName = useSpaceDisplayName();
  const months = report.unitsTrend
    ? buildTinesUnitsTrendChart(report.unitsTrend).columns.reverse()
    : [];
  const month = report.unitsTrend?.months.find(
    (point) => point.periodStart === periodStart
  );
  const { rows, total, max } = buildTinesUnitsBySpaceRows({
    usage: month?.spaces ?? [],
    matchesSpace,
    personalIds,
    foldPersonal: foldsPersonal(scope),
    spaceLabels: new Map(spaces.map((space) => [space.id, displayName(space)])),
  });
  const colors = spaceColorMap(spaces);
  colors.set(PERSONAL_BUCKET, PERSONAL_BUCKET_COLOR);

  return (
    <UnitsChartCard aria-label="Tines units by space">
      <Header>
        <PanelHeader>
          <Text variant="text-small-md">Tines units by space</Text>
          <TotalRow>
            <Text variant="display-md">{formatTinesUnits(total)}</Text>
          </TotalRow>
        </PanelHeader>
        <Dropdown
          align="bottom"
          opener={
            <ControlButton type="button">
              <Icon name="calendar" size={12} />
              <Text variant="text-small-md" ellipsis>
                {unitsMonthLabel.format(periodStart)}
              </Text>
              <Icon
                name="chevron-down"
                size={12}
                color="var(--text-secondary)"
              />
            </ControlButton>
          }
          sections={[
            {
              items: months.map((month) => ({
                type: "button" as const,
                key: String(month.periodStart),
                left: null,
                label: unitsMonthLabel.format(month.periodStart),
                selected: month.periodStart === periodStart,
                right:
                  month.periodStart === periodStart ? (
                    <Icon
                      name="check"
                      size={12}
                      color="var(--text-secondary)"
                    />
                  ) : undefined,
                onClick: () => onSelectMonth(month.periodStart),
              })),
            },
          ]}
        />
      </Header>
      {rows.length === 0 ? (
        <Empty>
          <Text variant="text-small" color="var(--text-secondary)">
            No space usage in {unitsMonthLabel.format(periodStart)}.
          </Text>
        </Empty>
      ) : (
        <Chart>
          <FadeScrollColumns>
            {rows.map((row) => {
              const content = (
                <>
                  <SpaceBarRow>
                    <Bar
                      data-bar
                      style={{
                        background: colors.get(row.key) ?? "var(--gray-600)",
                        minHeight: 3,
                        height: `${logBarFraction(row.total, max) * 100}%`,
                      }}
                    />
                  </SpaceBarRow>
                  <ColumnMeta>
                    <Text
                      variant="text-small"
                      color="var(--text-secondary)"
                      ellipsis
                    >
                      {row.label}
                    </Text>
                    <Text variant="text-small-md">
                      {formatTinesUnits(row.total)}
                    </Text>
                  </ColumnMeta>
                </>
              );
              return row.key !== UNATTRIBUTED_UNITS_BUCKET ? (
                <Column
                  key={row.key}
                  data-bar-column
                  onPress={() => onOpenSpace(row.key)}
                  aria-label={`View usage in ${row.label}`}
                >
                  {content}
                </Column>
              ) : (
                <BarColumn key={row.key} data-bar-column>
                  {content}
                </BarColumn>
              );
            })}
          </FadeScrollColumns>
        </Chart>
      )}
    </UnitsChartCard>
  );
}
